/**
 * The compact breakpoint, in ONE place (TRBUD-61).
 *
 * `699` is the number the whole responsive design turns on: below it the
 * desktop grids are `display: none`, the `.responsive-cards` blocks appear, the
 * bottom tab bar appears, the tab bar disappears, and the type scale steps up a
 * notch. It is written in `styles/responsive.css` seven times and was written in
 * `App.tsx` twice more as a `useMediaQuery("(max-width: 699px)")` string typed
 * out by hand — so a screen wanting to know "am I on a phone?" had to retype a
 * magic number that lives in a stylesheet, and a future move of the breakpoint
 * would have had to find every copy.
 *
 * ── This hook is NOT wired into any screen yet, and that is deliberate ──
 *
 * The Phone* card variants it exists for live in `src/screens/*.tsx`, which
 * belong to other work in flight. Adopting it is a one-line substitution per
 * call site — `useMediaQuery("(max-width: 699px)")` becomes `useIsCompact()` —
 * and it can be done a screen at a time with no coordination, because the two
 * evaluate the identical query. Today it has one consumer, its own test.
 *
 * ── Why a hook rather than reading `window.innerWidth` ──
 *
 * `innerWidth` does not re-render anything when it changes, and under Chrome's
 * device emulation it reports the PANE width rather than the emulated layout
 * viewport — measured on 8 September 2026, `innerWidth` said 776 while the
 * layout viewport, the media queries and `documentElement.clientWidth` all said
 * 700. A component branching on it would have taken the desktop path on a
 * device that had already switched to the phone stylesheet. `matchMedia` is
 * evaluated by the same engine as the CSS, so the two can never disagree.
 */
import { useMediaQuery } from "./useMediaQuery";

/**
 * The last width, in CSS pixels, that counts as compact.
 *
 * Kept as a number rather than only as a query string so a caller that needs the
 * value itself — a chart deciding how many ticks to draw, say — does not have to
 * parse it back out of the media query.
 */
export const COMPACT_MAX_WIDTH = 699;

/**
 * The query string, built from the constant above.
 *
 * `styles/responsive.css` must use the same figure. There is no mechanism that
 * can enforce that from CSS's side, so `useIsCompact.test.tsx` reads the
 * stylesheet and asserts it — the check is cheap, it is pure, and the failure it
 * catches (a stylesheet breakpoint moved without the script's) is otherwise
 * silent and produces a band of widths showing phone cards inside desktop chrome.
 */
export const COMPACT_QUERY = `(max-width: ${COMPACT_MAX_WIDTH}px)`;

/**
 * True on a phone-width viewport — the same widths at which `responsive.css`
 * hides `.desktop-layout` and shows `.responsive-cards` and `.compact-nav`.
 *
 * It is a WIDTH question, not a device question. A desktop window dragged narrow
 * gets the compact answer, which is correct: the layout it is being asked about
 * is the one the stylesheet has already applied.
 */
export function useIsCompact(): boolean {
  return useMediaQuery(COMPACT_QUERY);
}
